const mediaKey = (id) => String(id ?? '').split('/').pop();

// Follow the approved variant reference order, never the currently visible DOM.
export function selectVariantMedia(items, variant) {
  const byId = new Map(items.map(item => [mediaKey(item.id), item]));
  if (variant.mediaIds?.length) {
    return [...new Set(variant.mediaIds.map(mediaKey))]
      .map(id => byId.get(id)).filter(Boolean);
  }
  const matchingColor = items.filter(item =>
    item.alt?.split('|')[0].trim().toLowerCase() === variant.title?.trim().toLowerCase());
  if (matchingColor.length) return matchingColor;
  const featured = byId.get(mediaKey(variant.featuredId));
  return featured ? [featured] : [];
}

function initialize(root, attempt = 0) {
  if (root.dataset.featuredReady) return;
  const $ = window.jQuery;
  if (!$?.fn?.slick) {
    if (attempt < 50) setTimeout(() => initialize(root, attempt + 1), 100);
    return;
  }
  const gallery = root.querySelector('.rumiloo-featured-gallery');
  if (!gallery) return;
  const isProductGallery = root.matches('[data-rumiloo-placemat], [data-rumiloo-stable-gallery]');
  const thumbs = root.querySelector('.rumiloo-featured-thumbs');
  const variants = JSON.parse(root.querySelector('[data-featured-variants]').textContent);
  const items = [...gallery.children].filter(el => el.dataset.featuredMediaId)
    .map(el => ({ id: el.dataset.featuredMediaId, alt: el.querySelector('img')?.alt || '', el }));
  const thumbById = new Map([...thumbs?.children || []].map(el => [el.dataset.featuredMediaId, el]));
  const $gallery = $(gallery);
  const $thumbs = thumbs ? $(thumbs) : null;
  const count = (key, fallback) => Number(thumbs?.dataset[key]) || fallback;

  function select(variantId) {
    const variant = variants.find(v => String(v.id) === String(variantId));
    if (!variant) return;
    const ordered = selectVariantMedia(items, variant);
    // Hide Image 2 in product-page and New Arrivals galleries, including thumbs.
    // The variant's approved media references remain unchanged.
    const selected = isProductGallery ? ordered.filter((_item, index) => index !== 1) : ordered;
    if (!selected.length) return;
    if ($gallery.hasClass('slick-initialized')) $gallery.slick('unslick');
    if ($thumbs?.hasClass('slick-initialized')) $thumbs.slick('unslick');
    gallery.replaceChildren(...selected.map(({ el }, index) => {
      el.classList.toggle('act', index === 0);
      return el;
    }));
    if (thumbs) thumbs.replaceChildren(...selected.map(({ id }) => thumbById.get(mediaKey(id))).filter(Boolean));
    const hero = selected[0].el.querySelector('img');
    if (hero) {
      hero.loading = 'eager';
      if (hero.dataset.srcset) hero.srcset = hero.dataset.srcset;
      if (hero.dataset.src) hero.src = hero.dataset.src;
    }
    $gallery.slick({
      rows: 0, slidesToShow: 1, slidesToScroll: 1, initialSlide: 0,
      infinite: false, autoplay: false, adaptiveHeight: !isProductGallery,
      arrows: gallery.dataset.nav === 'true', dots: false,
      nextArrow: '<button type="button" class="arrow-next" aria-label="Next product image"><i class="zmdi zmdi-chevron-right" aria-hidden="true"></i></button>',
      prevArrow: '<button type="button" class="arrow-prev" aria-label="Previous product image"><i class="zmdi zmdi-chevron-left" aria-hidden="true"></i></button>'
    });
    if ($thumbs) {
      $thumbs.slick({
        rows: 0, slidesToShow: count('items', 4), slidesToScroll: 1,
        infinite: false, autoplay: false, arrows: false, dots: false,
        vertical: thumbs.dataset.vertical === 'true',
        responsive: [
          { breakpoint: 1200, settings: { slidesToShow: count('items_lg', 4), vertical: thumbs.dataset.vertical_lg === 'true' } },
          { breakpoint: 992, settings: { slidesToShow: count('items_md', 3), vertical: false } },
          { breakpoint: 768, settings: { slidesToShow: count('items_sm', 4), vertical: false } },
          { breakpoint: 576, settings: { slidesToShow: count('items_xs', 3), vertical: false } }
        ]
      });
      $thumbs.find('.item').removeClass('active').first().addClass('active');
    }
    root.dataset.featuredReady = 'true';
    root.dataset.activeVariant = String(variant.id);
  }

  $gallery.on('afterChange.rumiloo', (_event, _slick, index) => {
    $gallery.find('.item').removeClass('act').eq(index).addClass('act');
    if ($thumbs?.hasClass('slick-initialized')) {
      $thumbs.slick('slickGoTo', index);
      $thumbs.find('.item').removeClass('active').eq(index).addClass('active');
    }
  });
  $thumbs?.on('click.rumiloo', '.item', function (event) {
    event.preventDefault();
    const index = [...thumbs.querySelectorAll('.item')].indexOf(this);
    if (index >= 0) $gallery.slick('slickGoTo', index);
  });
  root.addEventListener('rumiloo:featured-variant', event => select(event.detail.variantId));
  select(root.dataset.initialVariant);
}

if (typeof document !== 'undefined') {
  const boot = (scope = document) => scope.querySelectorAll('[data-rumiloo-featured-product]').forEach(root => initialize(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('rumiloo:quick-view-open', event => boot(event.target));
}
